import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSocketStore } from '../stores/socketStore';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Users, 
  Music2, 
  Play, 
  Gamepad2,
  RefreshCw,
  Search,
  Loader2,
  Sparkles
} from 'lucide-react';

export default function GamesPage() {
  const navigate = useNavigate();
  const { publicRooms, fetchPublicRooms, joinRoom, isConnected } = useSocketStore();
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'waiting' | 'playing'>('all');

  // Charger les parties au montage et toutes les 5 secondes
  useEffect(() => {
    if (isConnected) {
      fetchPublicRooms();
      setIsLoading(false);
      
      const interval = setInterval(() => {
        fetchPublicRooms();
      }, 5000);
      
      return () => clearInterval(interval);
    }
  }, [isConnected, fetchPublicRooms]);

  // Filtrer les rooms
  const filteredRooms = publicRooms.filter(room => {
    // Filtre par recherche
    const matchesSearch = room.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         room.hostName?.toLowerCase().includes(searchQuery.toLowerCase());
    
    // Filtre par type
    if (filter === 'waiting') return matchesSearch && !room.isAutoRoom;
    if (filter === 'playing') return matchesSearch && room.isAutoRoom;
    
    return matchesSearch;
  });

  const handleJoinRoom = (code: string) => {
    joinRoom(code);
    navigate(`/room/${code}`);
  };

  const handleRefresh = () => {
    setIsLoading(true);
    fetchPublicRooms();
    setTimeout(() => setIsLoading(false), 500);
  };

  // Statistiques
  const totalPlayers = publicRooms.reduce((acc, room) => acc + room.playerCount, 0);
  const autoRooms = publicRooms.filter(r => r.isAutoRoom).length;

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="text-center mb-8">
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="inline-flex items-center gap-3 bg-gradient-to-r from-primary-500/20 to-secondary-500/20 px-6 py-3 rounded-2xl mb-4"
        >
          <Gamepad2 className="w-8 h-8 text-primary-400" />
          <h1 className="font-display text-3xl font-bold gradient-text">
            Parties en cours
          </h1>
        </motion.div>
        <p className="text-dark-400">
          Rejoignez une partie existante ou créez la vôtre !
        </p>
      </div>

      {/* Stats rapides */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="card text-center py-4"
        >
          <div className="text-2xl font-bold text-primary-400">{publicRooms.length}</div>
          <div className="text-dark-400 text-sm">Parties</div>
        </motion.div>
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="card text-center py-4"
        >
          <div className="text-2xl font-bold text-success">{totalPlayers}</div>
          <div className="text-dark-400 text-sm">Joueurs</div>
        </motion.div>
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="card text-center py-4"
        >
          <div className="text-2xl font-bold text-secondary-400">{autoRooms}</div>
          <div className="text-dark-400 text-sm">Auto-rooms</div>
        </motion.div>
      </div>

      {/* Barre de recherche et filtres */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-dark-400" />
          <input
            type="text"
            placeholder="Rechercher une partie..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="input pl-10 w-full"
          />
        </div>
        
        <div className="flex gap-2">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              filter === 'all'
                ? 'bg-primary-500 text-white'
                : 'bg-dark-700 text-dark-300 hover:bg-dark-600'
            }`}
          >
            Toutes
          </button>
          <button
            onClick={() => setFilter('waiting')}
            className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2 ${
              filter === 'waiting'
                ? 'bg-success/20 text-success border border-success/50'
                : 'bg-dark-700 text-dark-300 hover:bg-dark-600'
            }`}
          >
            <Users className="w-4 h-4" />
            Joueurs
          </button>
          <button
            onClick={() => setFilter('playing')}
            className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2 ${
              filter === 'playing'
                ? 'bg-secondary-500/20 text-secondary-400 border border-secondary-500/50'
                : 'bg-dark-700 text-dark-300 hover:bg-dark-600'
            }`}
          >
            <Sparkles className="w-4 h-4" />
            Auto
          </button>
          <button
            onClick={handleRefresh}
            className="p-2 bg-dark-700 hover:bg-dark-600 rounded-lg transition-colors"
            title="Actualiser"
          >
            <RefreshCw className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Liste des parties */}
      {isLoading ? (
        <div className="text-center py-20">
          <Loader2 className="w-12 h-12 text-primary-500 mx-auto mb-4 animate-spin" />
          <p className="text-dark-400">Chargement des parties...</p>
        </div>
      ) : filteredRooms.length === 0 ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center py-20"
        >
          <Gamepad2 className="w-16 h-16 text-dark-500 mx-auto mb-4" />
          <h2 className="font-display text-xl font-bold mb-2">
            {searchQuery ? 'Aucune partie trouvée' : 'Aucune partie en cours'}
          </h2>
          <p className="text-dark-400 mb-6">
            {searchQuery 
              ? 'Essayez avec d\'autres termes de recherche'
              : 'Soyez le premier à créer une partie !'
            }
          </p>
          <Link to="/create" className="btn-primary inline-flex items-center gap-2">
            <Play className="w-5 h-5" />
            Créer une partie
          </Link>
        </motion.div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          <AnimatePresence mode="popLayout">
            {filteredRooms.map((room, index) => (
              <motion.div
                key={room.code}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ delay: index * 0.05 }}
                className="card hover:border-primary-500/50 transition-all group cursor-pointer"
                onClick={() => handleJoinRoom(room.code)}
              >
                {/* Header de la carte */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-display font-bold text-lg truncate group-hover:text-primary-400 transition-colors">
                      {room.name}
                    </h3>
                    <p className="text-dark-400 text-sm truncate">
                      par {room.hostName || 'Anonyme'}
                    </p>
                  </div>
                  
                  {/* Badge de type */}
                  <div className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${
                    room.isAutoRoom
                      ? 'bg-secondary-500/20 text-secondary-400'
                      : 'bg-success/20 text-success'
                  }`}>
                    {room.isAutoRoom ? (
                      <>
                        <Sparkles className="w-3 h-3" />
                        Auto
                      </>
                    ) : (
                      <>
                        <Users className="w-3 h-3" />
                        Joueurs
                      </>
                    )}
                  </div>
                </div>

                {/* Mode de jeu */}
                <div className="flex items-center gap-2 mb-3 p-2 bg-dark-700/50 rounded-lg">
                  <Music2 className="w-4 h-4 text-primary-400 flex-shrink-0" />
                  <span className="text-sm text-dark-300 truncate capitalize">
                    Mode: {room.mode || 'Classique'}
                  </span>
                </div>

                {/* Footer avec infos */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 text-sm text-dark-400">
                    <div className="flex items-center gap-1">
                      <Users className="w-4 h-4" />
                      <span>{room.playerCount}/{room.maxPlayers || 10}</span>
                    </div>
                  </div>
                  
                  <button 
                    className="btn-primary text-sm py-1.5 px-4 opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleJoinRoom(room.code);
                    }}
                  >
                    Rejoindre
                  </button>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}

      {/* Bouton créer en bas */}
      {filteredRooms.length > 0 && (
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="text-center mt-8"
        >
          <Link to="/create" className="btn-secondary inline-flex items-center gap-2">
            <Play className="w-5 h-5" />
            Créer ma propre partie
          </Link>
        </motion.div>
      )}
    </div>
  );
}
